import { useEffect, useMemo, useRef } from "react";
import type { QueryFilters, QueryKey } from "@tanstack/react-query";
import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { realtimeScopeId, realtimeTopic, type PgChangePayload } from "@/lib/realtime/channel";

/**
 * A `documents` row change is already delivered to DocumentStore, which keeps
 * the document list itself live. What it never did was tell the react-query
 * caches that mirror the same rows — so the dashboard donut, the upload
 * charts, "Uploaded today" and the project activity counts sat on figures
 * from page load, in some cases indefinitely (nothing invalidated
 * `building-doc-stats` anywhere in the app).
 *
 * This bridges the two. It is a separate channel from DocumentStore's, on
 * purpose: the store holds a single channel slot claimed by whichever
 * document surface mounted last, and a third claimant would evict the second.
 *
 * Events are treated as a bare "something changed" signal — the pipeline walks
 * each row through several states, so what matters is that the aggregates are
 * now suspect, not which column moved.
 */

/** Long enough to absorb a bulk upload, short enough that one upload feels live. */
const WINDOW_MS = 1_000;

/**
 * Building ids reach these query keys as both strings and numbers depending on
 * the hook, so scope by value rather than by identity.
 */
function keyMatches(key: QueryKey, index: number, value: string | number): boolean {
  return String(key[index]) === String(value);
}

/**
 * Every project a changed row touched — BOTH halves of the payload, not the
 * first one that carries an id: `documents` is REPLICA IDENTITY FULL, so an
 * UPDATE that moves a document between projects names the destination in
 * `new` and the source in `old`, and the source's cache still holds the row.
 *
 * `project_id` is nullable (a building-level document belongs to no project),
 * so an empty result is a normal outcome, not a failure to resolve.
 */
function payloadProjectIds(payload: PgChangePayload): string[] {
  const ids: string[] = [];
  for (const row of [payload.new, payload.old]) {
    const projectId = (row as { project_id?: string | number | null } | undefined)?.project_id;
    if (projectId !== null && projectId !== undefined) ids.push(String(projectId));
  }
  return ids;
}

/**
 * Consumers riding this channel rather than opening one of their own — see
 * `useDocumentsRealtimeFlush`. Module-level because the bridge is mounted by
 * `BuildingLayout`, above every route that would register one.
 */
const flushSubscribers = new Set<() => void>();

/** Copied before the sweep: a subscriber may unregister while it runs. */
function notifyFlushSubscribers(): void {
  for (const subscriber of [...flushSubscribers]) {
    // Per subscriber: one throwing consumer must not cost the others their
    // refresh, and this runs inside the coalescer's flush.
    try {
      subscriber();
    } catch (e) {
      console.error("[realtime] documents flush subscriber threw", e);
    }
  }
}

/**
 * Refresh something on the same `documents` events — and in the same coalescing
 * window — the bridge already receives, instead of opening a second channel on
 * the same rows.
 *
 * For state react-query does not hold (the MST store's counts, tags and filter
 * values), which is why this is a callback rather than another entry in the
 * bridge's `invalidate` list. Scope stays the caller's: the events say only
 * that documents in this building changed.
 *
 * Only fires while the bridge is mounted and subscribed, i.e. under
 * `BuildingLayout`. That is every document surface in the app; a consumer
 * outside it needs its own subscription.
 */
export function useDocumentsRealtimeFlush(onFlush: () => void, enabled = true): void {
  // Through a ref so an inline arrow does not add and drop a subscriber on
  // every render.
  const onFlushRef = useRef(onFlush);
  useEffect(() => {
    onFlushRef.current = onFlush;
  });

  useEffect(() => {
    if (!enabled) return;
    const subscriber = () => onFlushRef.current();
    flushSubscribers.add(subscriber);
    return () => {
      flushSubscribers.delete(subscriber);
    };
  }, [enabled]);
}

/**
 * Keep every building-scoped react-query cache that counts documents in step
 * with the pipeline. Mount once per building — `BuildingLayout` is the single
 * place every one of these consumers renders under.
 */
export function useDocumentsRealtimeBridge(buildingId: string | number | null | undefined): void {
  // Null for anything that is not a real building id — a non-numeric route
  // param would otherwise subscribe as `building_id=eq.NaN` (rejected by
  // postgres_changes) and a whitespace one as `eq.0` (joins, matches nothing).
  // Either way every cache below sits stale with nothing on screen to say so,
  // which is the failure this hook exists to prevent.
  const numericBuildingId = realtimeScopeId(buildingId);
  const enabled = numericBuildingId !== null;

  // Projects touched since the last flush. `dwg-candidates` is keyed by org and
  // project, never by building, so the events themselves are the only scope
  // available — and project ids are unique, so they scope it on their own.
  const touchedProjectIds = useRef(new Set<string>());
  // A join or re-join landed in this window, so events were lost and no set of
  // project ids can describe what changed. Tracked separately from an empty
  // set, which is a real answer: a building-level document names no project.
  const resynced = useRef(false);

  const invalidate = useMemo<QueryFilters[]>(() => {
    if (numericBuildingId === null) return [];
    const id = numericBuildingId;
    return [
      // Seven sub-queries feed the dashboard donut and the upload charts, all
      // carrying the building id third.
      {
        queryKey: ["building-doc-stats"],
        predicate: (query) => keyMatches(query.queryKey, 2, id),
      },
      {
        queryKey: ["uploaded-today"],
        predicate: (query) => keyMatches(query.queryKey, 1, id),
      },
      {
        queryKey: ["building-storage-quota"],
        predicate: (query) => keyMatches(query.queryKey, 1, id),
      },
      {
        queryKey: ["building-projects-activity"],
        predicate: (query) => keyMatches(query.queryKey, 1, id),
      },
      // Franklin's logo (`franklinMade`): each brief confirm files its PDF as a
      // new row, which the cached id set would not know.
      {
        queryKey: ["franklin-made-documents"],
        predicate: (query) => keyMatches(query.queryKey, 1, id),
      },
      // staleTime: Infinity, deliberately — a document's parsed geometry never
      // changes. What does change is whether a new one has finished
      // processing, which is exactly this event. It is also why the join
      // itself flushes (`flushOnJoin` below): this subscription is the only
      // path back to the database for these two, so every window in which the
      // bridge was unmounted — any non-building route — is a gap nothing else
      // would ever correct.
      {
        queryKey: ["ifc-model-candidates"],
        predicate: (query) => keyMatches(query.queryKey, 1, id),
      },
      // Scoped to the projects the events named, so one building's upload
      // cannot flush another org's drawing picker. A row with no project is
      // not "unresolved": `useDwgCandidates` only ever fetches with a project
      // id and filters on it, so such a row is in none of these entries and
      // the flush is skipped. Only a join or re-join widens it back out — that
      // gap could have held anything.
      {
        queryKey: ["dwg-candidates"],
        predicate: (query) =>
          resynced.current || touchedProjectIds.current.has(String(query.queryKey[2])),
      },
    ];
  }, [numericBuildingId]);

  useRealtimeInvalidate({
    enabled,
    topic: realtimeTopic("documents-bridge", numericBuildingId),
    bindings: [
      {
        event: "*",
        table: "documents",
        filter: `building_id=eq.${numericBuildingId}`,
        // Runs before the coalescer is called, so the leading-edge flush
        // already sees this row's project.
        handler: (payload) => {
          for (const projectId of payloadProjectIds(payload)) {
            touchedProjectIds.current.add(projectId);
          }
        },
      },
    ],
    invalidate,
    // The caches above outlive this hook (react-query holds them for the whole
    // gcTime, and two of them never go stale on their own), so a mount is a
    // gap, not a fresh start: whatever the pipeline finished while the last
    // building route was unmounted is already sitting in them.
    flushOnJoin: true,
    // Fires before the flush it triggers — on the initial join as well as a
    // re-join — so the widened predicate is in place by the time react-query
    // evaluates it.
    onResync: () => {
      resynced.current = true;
    },
    // Fires after the invalidations of the same flush, so the scope is cleared
    // only once the predicate above has used it.
    onChange: () => {
      touchedProjectIds.current.clear();
      resynced.current = false;
      // After the clear, so a subscriber that throws cannot leave the next
      // flush inheriting this one's scope.
      notifyFlushSubscribers();
    },
    windowMs: WINDOW_MS,
  });

  // Both refs outlive the subscription: a building change (or an unmount) mid
  // window tears the channel down through the coalescer's `cancel()`, which
  // skips the pending flush and therefore the `onChange` that clears them. The
  // next building's first flush would inherit the previous one's project ids —
  // or a stale `resynced`, widening it to every org's dwg picker.
  useEffect(
    () => () => {
      touchedProjectIds.current.clear();
      resynced.current = false;
    },
    [buildingId]
  );
}
